import type React from 'react';
import type { CSSProperties } from 'react';
import { Outlet } from '@tanstack/react-router';
import { PanelLeft } from 'lucide-react';
import {
  Button,
  Loader,
  Sidebar,
  ProfileDialog,
  PageHeaderDashboard,
} from 'shared-portal-components-v2';

import { useAppLayout } from '@/hooks/layout/useAppLayout';
import { SidebarContent } from '@/components/molecules/SidebarContent/SidebarContent';
import ManageAccessModal from '@/components/organism/ManageAccess/ManageAccessModal';
import CreateSixModal from '@/components/organism/CreateSix/CreateSixModal';
import FileDetailModalContainer from '@/components/organism/FileDetailModal/FileDetailModalContainer';
import FileUploadModalContainer from '@/components/organism/FileUploadModal/FileUploadModalContainer';
import CreateFolderModalContainer from '@/components/organism/CreateFolderModal/CreateFolderModalContainer';

type LayoutCssVariables = Record<'--sidebar-width' | '--sidebar-width-icon', string>;

const sidebarCssVariables: CSSProperties & LayoutCssVariables = {
  '--sidebar-width': '20rem',
  '--sidebar-width-icon': '3.5rem',
};

const DESKTOP_SIZE_BREAKPOINT = 1024;

export function AppLayout(): React.JSX.Element {
  const { state, handlers } = useAppLayout();

  return (
    <div className="flex min-h-svh w-full bg-background" style={sidebarCssVariables}>
      {/* Mobile overlay */}
      {state.mobileMenuIsOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/80 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={handlers.handleMobileOverlayClick}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar - slide in/out */}
      <div
        className={[
          'fixed inset-y-0 left-0 z-50 w-60 bg-sidebar border-r border-border transition-transform duration-300 ease-in-out lg:hidden',
          state.mobileMenuIsOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <Sidebar
          collapsed={false}
          collapsible="offcanvas"
          logo={
            <div className="flex items-center justify-center py-4">
              {state.sidebarConfig.logoNode}
            </div>
          }
          className="h-full border-0"
        >
          {() => (
            <SidebarContent
              prefix="mobile"
              sections={state.sidebarConfig.sections}
              footer={state.sidebarConfig.footer}
              onAction={handlers.handleSidebarAction}
            />
          )}
        </Sidebar>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <Sidebar
          collapsed={state.sidebarIsCollapsed}
          collapsible="offcanvas"
          logo={
            <div className="flex items-center justify-center py-4">
              {state.sidebarConfig.logoNode}
            </div>
          }
          className="h-full"
        >
          {() => (
            <SidebarContent
              prefix="desktop"
              sections={state.sidebarConfig.sections}
              footer={state.sidebarConfig.footer}
              onAction={handlers.handleSidebarAction}
            />
          )}
        </Sidebar>
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0">
        <div className="flex h-16 items-center px-6 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-30">
          <Button
            variant="outline"
            size="icon"
            onClick={handlers.handleSidebarToggle}
            title={state.sidebarIsCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            aria-label={state.sidebarIsCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            className="h-7 w-7 mr-4"
          >
            {state.sidebarIsCollapsed ||
            (!state.mobileMenuIsOpen && window.innerWidth < DESKTOP_SIZE_BREAKPOINT) ? (
              <PanelLeft className="h-4 w-4" />
            ) : (
              <PanelLeft className="h-4 w-4" />
            )}
          </Button>

          <PageHeaderDashboard
            organizationName={state.organizationName}
            className="flex-1 border-b-0 bg-transparent px-0 h-16"
            notifications={state.headerNotifications}
            unreadCount={state.unreadCount}
            user={{
              name: state.userName,
              email: state.userEmail,
              avatarUrl: state.avatarSrc,
            }}
            onHelpClick={handlers.handleHelpClick}
            onSelectProfile={handlers.handleSelectProfile}
            onSelectLogout={handlers.handleSelectLogout}
          />
        </div>

        <div className="flex-1">
          <Outlet />
        </div>
      </main>

      <ManageAccessModal />
      <CreateSixModal />
      <FileDetailModalContainer />
      <FileUploadModalContainer />
      <CreateFolderModalContainer />
      <ProfileDialog
        isOpen={state.profileDialogIsOpen}
        onClose={handlers.handleCloseProfileDialog}
        user={state.profileUser}
        onSave={handlers.handleSaveProfile}
        onVerify={handlers.handleVerifyPhone}
        onResend={handlers.handleResendCode}
      />
      <Loader isVisible={state.isLoaderVisible} />
    </div>
  );
}

export default AppLayout;
